import React, { useContext, useState } from 'react';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { CreditApprovalRound, CreditApprovalStepType } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { DecisionModal } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/DecisionModal/DecisionModal';
import {
  escalateCreditApprovalRound,
  getCreditApprovalData
} from 'features/brazil-grower/queries/creditApproval.queries';
import { utils } from 'shared';

const APPROVAL_STEP_TYPES_COUNT = Object.keys(CreditApprovalStepType).length;

interface EscalateCreditApprovalV2Props {
  round?: CreditApprovalRound;
}

export const EscalateCreditApprovalV2: React.FunctionComponent<EscalateCreditApprovalV2Props> = ({ round }) => {
  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  /**
   * Mutations
   */
  const [escalateCreditApprovalRoundFn] = useMutation(escalateCreditApprovalRound);

  /**
   * Context
   */
  const { agFinancingId } = useContext(FinanceProcessContext);

  const { addFlashMessage } = useFlashMessage();

  // Round completed
  if (utils.CreditApprovalUtil.isRoundComplete(round)) {
    return null;
  }

  const escalateCreditApproval = (escalationNotes: string) => {
    if (!round || !selectedRole) {
      return Promise.resolve();
    }

    const l3ApprovalRequired = selectedRole === 'Gerente';
    return escalateCreditApprovalRoundFn({
      variables: {
        data: {
          agFinancingId,
          escalationNotes,
          roundId: round.id,
          l3ApprovalRequired
        }
      },
      refetchQueries: [
        {
          query: getCreditApprovalData,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        addFlashMessage({
          content: '',
          title: i18next.t('successfullyEscalated'),
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  return round?.steps?.length && round.steps.length < APPROVAL_STEP_TYPES_COUNT ? (
    <>
      <DecisionModal
        title={i18next.t('escalate')}
        onConfirmAction={escalateCreditApproval}
        open={isDecisionOpen}
        closeModal={() => setIsDecisionOpen(false)}
        notesRequired
        canSave={!!selectedRole}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '60%', marginBottom: 20 }}>
          <label>
            <input
              type="radio"
              name="role"
              value="Gerente"
              checked={selectedRole === 'Gerente'}
              onChange={() => setSelectedRole('Gerente')}
              required
            />{' '}
            {i18next.t('manager')}
          </label>
          <label>
            <input
              type="radio"
              name="role"
              value="Comitê"
              checked={selectedRole === 'Comitê'}
              onChange={() => setSelectedRole('Comitê')}
              required
            />{' '}
            {i18next.t('committee')}
          </label>
        </div>
      </DecisionModal>
      <Button
        variant="secondary"
        size="small"
        onClick={() => setIsDecisionOpen(true)}
        className="text-transform-capitalize"
      >
        {i18next.t('escalate')}
      </Button>
    </>
  ) : null;
};
